import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { phoneImage } from "@/shared/assets";

import type { AnsweredDevice, RegisteredDevice } from "../api";

import { normalizeGrade, parsePrice } from "../lib";

/** Оценка сдаваемого устройства: регистрация в it4, ответ о состоянии, цена выкупа */
export const useEvaluationStore = defineStore("evaluation", () => {
    // |-------|
    // | STATE |
    // |-------|

    const registeredDevice = ref<RegisteredDevice | null>(null);
    const answeredDevice = ref<AnsweredDevice | null>(null);
    /** Вариант ответа о состоянии, который выбрал пользователь */
    const answeredOptionId = ref<string | null>(null);

    // |----------|
    // | COMPUTED |
    // |----------|

    /** Устройство принимается в Trade-In: у него есть цена выкупа */
    const isAccepted = computed(
        () =>
            registeredDevice.value !== null &&
            !Number.isNaN(Number.parseFloat(registeredDevice.value.price)),
    );

    /** Грейд по ответу о состоянии */
    const answeredGrade = computed(() =>
        answeredDevice.value ? normalizeGrade(answeredDevice.value.grade) : null,
    );

    /** Предварительная цена выкупа для частного лица (B2C), null - пока пользователь не оценил состояние */
    const tradeInPrice = computed<number | null>(() =>
        answeredDevice.value ? parsePrice(answeredDevice.value.price) : null,
    );

    const currency = computed(() => registeredDevice.value?.currency ?? "");

    const deviceImage = computed(() => registeredDevice.value?.tradeinThumbnail || phoneImage);

    // |---------|
    // | METHODS |
    // |---------|

    function setRegisteredDevice(device: RegisteredDevice): void {
        reset();
        registeredDevice.value = device;
    }

    function setAnsweredDevice(device: AnsweredDevice, optionId: string): void {
        answeredDevice.value = device;
        answeredOptionId.value = optionId;
    }

    function reset(): void {
        registeredDevice.value = null;
        answeredDevice.value = null;
        answeredOptionId.value = null;
    }

    return {
        registeredDevice,
        answeredDevice,
        answeredOptionId,

        isAccepted,
        answeredGrade,
        tradeInPrice,
        currency,
        deviceImage,

        setRegisteredDevice,
        setAnsweredDevice,
        reset,
    };
});
